import { createLabManifest } from "@/helpers/lab_utils/labManifest";
import {
  createLabPassage,
  completionPassageExtras
} from "@/helpers/lab_utils/labPassage";
import colorimeter from "@/assets/Vernier Colorimeter.jpg";
import principle from "@/assets/Principle.jpg";

const passages = [
  createLabPassage({
    id: "start",
    progressLabel: "Welcome",
    title: "Analytical Instrumentation in ChE, BioE and EnvE",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Welcome to CHE 417/517: Analytical Instrumentation Laboratory!"
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Laboratory 1: Colorimetric determination of the concentration of Fe(II) in solution"
                }
              ]
            }
          ]
        }
      },
      {
        type: "custom",
        name: "skip-to-measurements",
        skipTo: "wavelength-selection"
      },
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "The purpose of this experiment is to:"
                }
              ]
            },
            {
              type: "orderedList",
              attrs: { start: 1 },
              content: [
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Use a practical implementation of chemical derivatization to produce a species giving a strong instrumental signal from one that gives only a weak signal"
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Introduce you to the 'calibration curve' methodology for carrying out an analytical concentration measurement"
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Carry out a concentration determination by colorimetry"
                        }
                      ]
                    }
                  ]
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [
      {
        text: "Next",
        to: "introduction"
      }
    ]
  }),

  createLabPassage({
    id: "introduction",
    progressLabel: "Introduction",
    title: "Introduction",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "In this experiment, you have a set of pre-prepared Fe(II) solutions of known concentrations along with one unknown. Using the colorimeter, you will measure how much light each solution absorbs at different wavelengths to build an absorption profile."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "The wavelength showing the strongest absorption ("
                },
                {
                  type: "inlineMath",
                  attrs: {
                    latex: "\\lambda_{\\max}"
                  }
                },
                {
                  type: "text",
                  text: ") will be determined. Measuring the absorbance at "
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "\\lambda_{\\max}" }
                },
                {
                  type: "text",
                  text: " for each standard solution enables the construction of a calibration curve, with the slope being the molar absorptivity. You will use this calibration to determine the concentration of an unknown Fe(II) solution."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [
      {
        text: "Next",
        to: "spectrophotometer"
      }
    ]
  }),
  createLabPassage({
    id: "spectrophotometer",
    progressLabel: "Spectrophotometer",
    title: "The Spectrophotometer",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "A spectrophotometer measures the amount of radiant energy absorbed by a species. A common spectrophotometer consists of a light source (incandescent filament) that emits a continuous range of wavelengths. The radiant energy is focused by optics which includes a prism or grating; these define a beam of radiant energy of a specific wavelength range."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "The detector determines the intensity of the radiation before and after the beam is passed through the sample in a cell. The sample may be a gas, liquid, solid or a solution containing an absorbing species such as the permanganate ion. The computer-interfaced colorimeter monitors the light received by the photocell as either an absorbance or a percent transmittance value."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [
      {
        text: "Next",
        to: "colorimeter-intro"
      }
    ]
  }),

  createLabPassage({
    id: "colorimeter-intro",
    progressLabel: "Colorimeter",
    title: "The Colorimeter",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "In today's lab, instead of a full spectrophotometer, you will be using a 'four color colorimeter' – it is essentially the same instrument as the spectrophotometer, but instead of using white light followed by a monochromator to select the wavelength of interest, it uses a very narrow emission LED as a light source without monochromator."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Naturally, the simpler design of the colorimeter results in a much less expensive instrument, but it comes with natural limitations, most notably, the fact that measurements can only be used at specific wavelengths (430, 470, 565, and 635 nm)."
                }
              ]
            }
          ]
        }
      },
      {
        type: "image",
        src: colorimeter,
        caption:
          "Vernier Colorimeter - Four-wavelength LED-based instrument for absorbance measurements"
      }
    ],
    choices: [
      {
        text: "Next",
        to: "absorption"
      }
    ]
  }),

  createLabPassage({
    id: "absorption",
    progressLabel: "Absorption Theory",
    title: "The Absorption of Radiant Energy",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "The nature of the species, the wavelength of light and the number of absorbing units in the path of the light all dictate the species' ability to absorb light. When using solutions, the number of absorbing units depends upon the concentration of the solution, and the path length of radiant energy through the sample."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "The transmittance ("
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "T" }
                },
                {
                  type: "text",
                  text: ") of the solution is the ratio of the transmitted power to the incident power:"
                }
              ]
            },
            {
              type: "blockMath",
              attrs: { latex: "T = \\frac{I}{I_0}" }
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Where:"
                },
                {
                  type: "hardBreak"
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "I_0" }
                },
                {
                  type: "text",
                  text: " = power of the incident light"
                },
                {
                  type: "hardBreak"
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "I" }
                },
                {
                  type: "text",
                  text: " = power of the light transmitted by (or exiting from) the absorbing medium"
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "The absorbance ("
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "A" }
                },
                {
                  type: "text",
                  text: ") is a log function of the transmittance:"
                }
              ]
            },
            {
              type: "blockMath",
              attrs: { latex: "A = -\\log_{10} T" }
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Non-absorbing solutions have "
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "T = 1.00" }
                },
                {
                  type: "text",
                  text: " (100%) and "
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "A = 0.00" }
                },
                {
                  type: "text",
                  text: " while complete absorption of light gives "
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "T = 0.00" }
                },
                {
                  type: "text",
                  text: " (0%) and "
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "A = \\infty" }
                },
                {
                  type: "text",
                  text: " (in practice "
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "A" }
                },
                {
                  type: "text",
                  text: " will be up to 7 even in the best instruments: "
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "A = 4" }
                },
                {
                  type: "text",
                  text: " implies absorption of 99.99% of light)."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [
      {
        text: "Next",
        to: "beer-lambert"
      }
    ]
  }),

  createLabPassage({
    id: "beer-lambert",
    progressLabel: "Beer-Lambert Law",
    title: "Beer-Lambert Law",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "The absorbance of species "
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "x" }
                },
                {
                  type: "text",
                  text: " in solution at wavelength "
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "\\lambda" }
                },
                {
                  type: "text",
                  text: " is proportional to its concentration and the path length. The relation is expressed in the form of the Beer-Lambert Law (or Beer's Law):"
                }
              ]
            },
            {
              type: "blockMath",
              attrs: {
                latex:
                  "A_{x,\\lambda} = \\varepsilon_{\\lambda,x} \\cdot b \\cdot C_x"
              }
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Where:"
                },
                {
                  type: "hardBreak"
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "\\varepsilon_{\\lambda,x}" }
                },
                {
                  type: "text",
                  text: " = molar absorptivity, a constant whose value depends on the nature of the absorbing species and the wavelength of the light"
                },
                {
                  type: "hardBreak"
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "b" }
                },
                {
                  type: "text",
                  text: " = path length of the cuvette in which the sample is contained"
                },
                {
                  type: "hardBreak"
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "C" }
                },
                {
                  type: "text",
                  text: " = molar concentration of the absorbing species"
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Notice that the absorbance is linear with respect to concentration, which makes it particularly convenient. If the concentration of the absorbing species ("
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "C" }
                },
                {
                  type: "text",
                  text: ") is expressed in mol/L (M) and "
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "b" }
                },
                {
                  type: "text",
                  text: " in cm, "
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "\\varepsilon_\\lambda" }
                },
                {
                  type: "text",
                  text: " becomes the molar extinction coefficient with units "
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "\\text{M}^{-1}\\,\\text{cm}^{-1}" }
                },
                {
                  type: "text",
                  text: "."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Therefore, in order to be able to determine concentration of a solution from the measured absorbance, an important initial step is to create an instrumental response curve (a.k.a., a calibration curve) that correlates a known concentration of a solution to an absorbance measurement. The slope of this instrumental response curve is the molar extinction coefficient discussed above."
                }
              ]
            }
          ]
        }
      },
      {
        type: "image",
        src: principle,
        caption:
          "Beer-Lambert Law: Linear relationship between absorbance and concentration"
      }
    ],
    choices: [
      {
        text: "Next",
        to: "derivatization"
      }
    ]
  }),

  createLabPassage({
    id: "derivatization",
    progressLabel: "Chemical Derivatization",
    title:
      "Chemical Derivatization - What if your molecule doesn't absorb light?",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "The solution here is to 'derivatize' your analyte to give you a molecule that will absorb light (or, in general, will give you a molecule you can detect with whatever technique you are trying to use)."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "In today's experiment, Fe(II) will be the example material we will be analyzing for. The Fe(II) aqueous solution is not particularly strongly colored, and you may expect the experimental results not to be too reliable."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "The Fe(II) solution for these measurements are initially formed from an Fe(III) salt, and in order to be quantitative all the Fe(III) needs to be reduced to Fe(II) by the use of excess hydroxylamine hydrochloride:"
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "We will convert it to a molecule in which charge transfer occurs that is vastly more strongly colored."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "This is achieved by treatment of the "
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "\\text{Fe}^{2+}" }
                },
                {
                  type: "text",
                  text: " solution with 1,10-phenanthroline (phen) which converts the light green "
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "\\text{Fe}^{2+}" }
                },
                {
                  type: "text",
                  text: " to dark red "
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "[\\text{Fe}(\\text{phen})_3]^{2+}" }
                },
                {
                  type: "text",
                  text: " species:"
                }
              ]
            },
            {
              type: "blockMath",
              attrs: {
                latex:
                  "[\\text{Fe}(\\text{H}_2\\text{O})_6]^{2+}(\\text{aq}) + 3\\,\\text{phen} \\rightarrow [\\text{Fe}(\\text{phen})_3]^{2+}(\\text{aq}) + 6\\,\\text{H}_2\\text{O}"
              }
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "As mentioned, the much darker color results from the greater molar absorption coefficient of "
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "[\\text{Fe}(\\text{phen})_3]^{2+}" }
                },
                {
                  type: "text",
                  text: " due to the creation of a charge transfer transition."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [
      {
        text: "Next",
        to: "experiment"
      }
    ]
  }),

  createLabPassage({
    id: "experiment",
    progressLabel: "Experiment Overview",
    title: "The Experiment",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "You are measuring how much iron is in an unknown solution by using a colorimeter. Darker solutions absorb more light, and that absorbance can be related to concentration."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Each sample is placed into a small, rectangular cuvette that sits inside the colorimeter. The instrument measures how much of its light beam is absorbed by the solution, and reports that value as an absorbance reading."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "A graph of absorbance vs. concentration plotted for the standard solutions should give a linear relationship (as expected from Beer's Law). By using the molar extinction coefficient obtained from this calibration curve and the absorbance of the unknown, the corresponding concentration can be found."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [
      {
        text: "Next",
        to: "lab-samples"
      }
    ]
  }),

  createLabPassage({
    id: "lab-samples",
    progressLabel: "Lab Samples",
    title: "Lab Samples",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "The lab contains 11 prepared samples used to perform the experiment:"
                }
              ]
            },
            {
              type: "bulletList",
              content: [
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "One blank (water + reagent, no iron) — used to zero/calibrate the instrument"
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: '9 standard samples total: 3 concentrations (0.05 mM, 0.10 mM, and 0.20 mM) with 3 replicates each, labeled /"A/" through /"I/"'
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "One unknown sample that contains a mystery amount of iron"
                        }
                      ]
                    }
                  ]
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Every sample has a total volume of 20.00 mL. The blank is a mix of water and reagent with no iron. The standard solutions are pre-prepared "
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "[\\text{Fe}(\\text{phen})_3]^{2+}" }
                },
                {
                  type: "text",
                  text: " solutions at the three known concentrations. The unknown solution has an unknown concentration of iron."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [
      {
        text: "Next",
        to: "wavelength-selection"
      }
    ]
  }),

  createLabPassage({
    id: "wavelength-selection",
    progressLabel: "Wavelength Selection",
    title: "Step 1: Determine Optimal Wavelength",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Before collecting data, you need to find which wavelength produces the highest absorbance for the "
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "[\\text{Fe}(\\text{phen})_3]^{2+}" }
                },
                {
                  type: "text",
                  text: " complex. The colorimeter offers four wavelengths: 430 nm, 470 nm, 565 nm, and 635 nm."
                }
              ]
            },
            {
              type: "orderedList",
              attrs: { start: 1 },
              content: [
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Insert the blank solution into the colorimeter and calibrate the instrument. This sets the baseline."
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Set the instrument to 430 nm. Measure the standard solution with the highest concentration and record its absorbance."
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Repeat with the same standard sample at 470 nm, 565 nm, and 635 nm, recording the absorbance at each wavelength."
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Compare the absorbance values from the four wavelengths and choose the wavelength that gives the highest absorbance, use this wavelength for your following experiment in this lab."
                        }
                      ]
                    }
                  ]
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [
      {
        text: "Next",
        to: "data-collection"
      }
    ],
    setFlags: (prev) => ({
      ...prev,
      labAreaUnlocked: true
    })
  }),

  createLabPassage({
    id: "data-collection",
    progressLabel: "Standard Measurements",
    title: "Step 2: Measure Standard Samples for Calibration Curve",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Set the instrument to the wavelength that produced the highest absorbance and recalibrate using the blank solution. This ensures your actual data collection is done correctly at the optimal wavelength."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: 'Now measure the absorbance of all 9 standard samples ("A" through "I"). Measurements will automatically be recorded on the colorimeter\'s display. You should end with 3 readings for each of the three known concentrations.'
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [
      {
        text: "Next",
        to: "unknown-data"
      }
    ]
  }),

  createLabPassage({
    id: "unknown-data",
    progressLabel: "Unknown Measurement",
    title: "Step 3: Measure the Unknown",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Measure the unknown solution at the same wavelength three separate times and record all three absorbance values. Taking multiple measurements improves precision by reducing random measurement errors."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [
      {
        text: "Next",
        to: "data-review"
      }
    ]
  }),

  createLabPassage({
    id: "data-review",
    progressLabel: "Data Review",
    title: "Step 4: Watch for Imperfect Data",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Real instruments do not give perfect readings. Small differences between replicates are normal because of measurement noise, pipetting variation, and occasional outliers."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "If one reading looks very different from the others, note it and decide whether it should be excluded with justification."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [
      {
        text: "Next",
        to: "save-attempt"
      }
    ]
  }),

  createLabPassage({
    id: "save-attempt",
    progressLabel: "Save Attempt",
    title: "Step 5: Save Your Attempt",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "At the end of the activity, click save attempt. You will navigate to the attempts view where you can export your data to begin the analysis portion of the lab using the course's chosen spreadsheet application."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "You may attempt the activity a total of 5 times."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [
      {
        text: "Save Attempt",
        to: "lab-complete"
      }
    ]
  }),

  createLabPassage({
    id: "lab-complete",
    progressLabel: "Lab Complete",
    title: "Laboratory Complete",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Congratulations! You have successfully completed Laboratory 1: Colorimetric determination of Fe(II) concentration."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "In this activity you have:"
                }
              ]
            },
            {
              type: "bulletList",
              content: [
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Learned about colorimetry and the Beer-Lambert Law"
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Determined the optimal wavelength for analysis"
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Collected absorbance data for 9 calibration standards"
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Measured the absorbance of an unknown Fe(II) solution"
                        }
                      ]
                    }
                  ]
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Your exported data will be used to create calibration curves and determine the concentration of your unknown sample."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [],
    ...completionPassageExtras("lab-complete")
  })
];

/** @type {import("@/types/labManifest.types.js").LabManifest} */
export const labManifest = createLabManifest({
  id: "analytical-instrumentation-lab",
  displayName: "Analytical Instrumentation",
  labAreaUnlockPassageId: "wavelength-selection",
  completionPassageId: "lab-complete",
  passages
});

export default {
  draft: passages
};
export { passages };
